import type {
  ComputePositionConfig,
  Placement,
} from '@floating-ui/dom'
import type Button from '../button/Button.js'
import {
  autoPlacement,
  autoUpdate,
  computePosition,
  flip,
  hide,
  offset,
  shift,
} from '@floating-ui/dom'
import { html, LitElement } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { DirectionController } from '../common/controllers/DirectionController.js'
import { EventController } from '../common/controllers/EventController.js'
import { LightDismissController } from '../common/controllers/LightDismissController.js'
import { ScrollbarController } from '../common/controllers/ScrollbarController.js'

import { observe } from '../common/decorators/observe.js'
import { DISMISS_OVERLAYS_EVENT } from '../common/DismissOverlaysEvent.js'
import { NordEvent, transition } from '../common/events.js'
import { FloatingMixin } from '../common/mixins/FloatingComponentMixin.js'
import { logicalToPhysical } from '../common/positioning.js'
import componentStyle from '../common/styles/Component.css'
import style from './Popout.css'

/*
 * The breakpoint width to switch between "sheet" design and floating design
 */
const mediaQuery
  = typeof matchMedia === 'undefined'
    ? ({
        matches: false,
        addEventListener() {
          /* noop */
        },
      } as unknown as MediaQueryList)
    : matchMedia('(max-width: 35.9375em)')

/**
 * The portion of the layout viewport hidden below the visual viewport — the
 * on-screen keyboard, plus any floating accessory bar. Clamped to 0 so a larger
 * or offset visual viewport never yields a negative inset.
 */
export function computeKeyboardInset(innerHeight: number, viewport: { height: number, offsetTop: number }): number {
  return Math.max(0, innerHeight - viewport.height - viewport.offsetTop)
}

/**
 * Popouts are small overlays that open on demand. They let users access additional content and actions without cluttering the page.
 *
 * @status ready
 * @category overlay
 * @slot - The popout content.
 *
 * @cssprop [--n-popout-background=var(--n-color-surface)] - Background of the popout container. Set to `transparent` to suppress the popout's own surface (e.g. when a slotted element paints its own background).
 * @cssprop [--n-popout-box-shadow=var(--n-box-shadow-popout)] - Box-shadow of the popout container. Set to `none` to suppress the popout's own shadow.
 */
@customElement('nord-popout')
export default class Popout extends FloatingMixin(LitElement) {
  static styles = [componentStyle, style]

  private targetElement?: HTMLElement
  private anchorElement?: HTMLElement
  private cleanupAutoUpdate?: ReturnType<typeof autoUpdate>
  private cleanupViewportTracking?: () => void

  @query('.n-popout', true) private popout!: HTMLDivElement

  private scrollBar = new ScrollbarController(this)

  /**
   * Handle dismissal of the popout, clicking outside the target button and popout.
   */
  private dismiss = new LightDismissController(this, {
    isOpen: () => this.open,
    // Return focus to the trigger only on Escape. A pointer dismissal (`click`)
    // sent focus wherever the user clicked, and an overlay-open dismissal is
    // about to hand focus to the opening overlay — restoring focus to a trigger
    // now sitting behind a modal would fight the modal's focus trap.
    onDismiss: e => this.hide(e.type !== 'click' && e.type !== DISMISS_OVERLAYS_EVENT),
    isDismissible: node => node !== this.popout && node !== this.targetElement,
    dismissOnOverlayOpen: true,
  })

  private events = new EventController(this)
  private direction = new DirectionController(this)

  @state() private computedPosition?: Placement

  @state() private smallViewport = mediaQuery.matches

  /**
   * The id for the active element to reference via aria-controls.
   */

  @property({ reflect: true }) id: string = ''

  /**
   * Set an optional anchor element to align against, replacing the triggering element.
   */
  @property({ reflect: true }) anchor?: string

  /**
   * Set to true to always display the popout as a floating overlay, even on smaller viewports.
   */
  @property({ reflect: true, type: Boolean, attribute: 'always-floating' }) alwaysFloating: boolean = false

  /**
   * Set to true when the consuming component owns the `open` state and drives
   * the popout itself (e.g. via its own trigger `@click` and `show()`/`hide()`).
   * The popout stays presentational — positioning, top layer, light dismiss and
   * transitions all still work — but it does NOT attach its own click-to-toggle
   * listener to the `aria-controls` trigger, avoiding a competing second toggle
   * on the same click.
   */
  @property({ reflect: true, type: Boolean }) controlled: boolean = false

  /**
   * Show the popout.
   * A promise that resolves to a `TransitionEvent` when the popout's show animation ends or is cancelled.
   * If the popout is already open, the promise resolves immediately with `undefined`.
   */
  async show(): Promise<TransitionEvent | void> {
    if (this.open) {
      return Promise.resolve()
    }

    this.enterTopLayer()
    this.open = true

    // we should only focus once the popout is visible after render is complete
    await this.updateComplete

    /**
     * Dispatched when the popout is opened.
     */
    this.dispatchEvent(new NordEvent('open'))

    return transition(this.popout)
  }

  /**
   * Hide the popout.
   * Returns a promise that resolves to a `TransitionEvent` when the popout's hide animation ends or is cancelled.
   * If the popout is already closed, the promise resolves immediately with `undefined`.
   * @param {boolean} moveFocusToButton prevent focus returning to the target button. Default is true.
   */
  async hide(moveFocusToButton: boolean = true): Promise<TransitionEvent | void> {
    if (!this.open) {
      return Promise.resolve()
    }

    this.open = false

    this.cleanupAutoUpdate?.()

    /**
     * Dispatched when the popout is closed.
     */
    this.dispatchEvent(new NordEvent('close'))

    if (moveFocusToButton) {
      this.targetElement?.focus({ preventScroll: true })
    }

    const result = await transition(this.popout)
    this.leaveTopLayer()
    return result
  }

  /**
   * Position the popout on load.
   */
  firstUpdated() {
    if (!this.smallViewport || this.alwaysFloating) {
      this.updatePosition()
    }
  }

  connectedCallback() {
    super.connectedCallback()

    this.events.listen(mediaQuery, 'change', this.handleMediaQueryChange)

    this.initializeElements()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.cleanupAutoUpdate?.()
    this.stopKeyboardInsetTracking()
    this.setTargetExpanded()
  }

  render() {
    return html`
      <div
        class="n-popout ${this.computedPosition} is-${this.direction.dir}"
        aria-hidden=${this.open ? 'false' : 'true'}
        @transitionend=${this.enableScroll}
      >
        <slot></slot>
      </div>
    `
  }

  @observe('id')
  protected handleIdChange() {
    // clean up any old listeners
    this.targetElement?.removeEventListener('click', this.toggleOpen)

    if (!this.id) {
      console.warn('NORD: popout requires an id attribute and value')
    }
    else {
      this.initializeElements()
    }
  }

  private initializeElements() {
    this.targetElement = this.getToggle()
    this.anchorElement = this.getAnchor()

    // In controlled mode the consuming component owns the toggle, so the popout
    // resolves its trigger (for positioning, aria-expanded and light dismiss) but
    // does not attach its own click-to-toggle listener.
    if (!this.controlled) {
      this.events.listen(this.targetElement, 'click', this.toggleOpen)
    }
  }

  @observe('open')
  protected handleOpenChange() {
    this.setTargetExpanded(this.open)

    if (this.open) {
      this.enterTopLayer()

      if (this.smallViewport && !this.alwaysFloating) {
        // hide scrollbar and prevent scroll on body
        this.scrollBar.lockScroll()
        this.trackKeyboardInset()
      }
      else if (this.anchorElement) {
        this.cleanupAutoUpdate = autoUpdate(this.anchorElement, this, this.updatePosition)
      }
    }
    else {
      this.cleanupAutoUpdate?.()
      this.stopKeyboardInsetTracking()
    }
  }

  private setTargetExpanded(open?: boolean) {
    const target = this.targetElement
    if (!target) {
      return
    }
    if (target.localName === 'nord-button') {
      const button = target as Button
      button.accessibleExpanded = open == null ? undefined : `${open}`
      target.removeAttribute('aria-expanded')
      return
    }
    if (open == null) {
      target.removeAttribute('aria-expanded')
    }
    else {
      target.setAttribute('aria-expanded', `${open}`)
    }
  }

  @observe('anchor')
  protected handleAnchorChange() {
    this.cleanupAutoUpdate?.()
    this.anchorElement = this.getAnchor()

    // if the popout is already open when the anchor changes, we should update its position
    if (this.open && (!this.smallViewport || this.alwaysFloating) && this.anchorElement) {
      this.cleanupAutoUpdate = autoUpdate(this.anchorElement, this, this.updatePosition)
    }
  }

  private enableScroll = () => {
    // scrollbar should only be restored when the backdrop has transitioned
    // that way we avoid awkward double scrollbars.
    if (!this.open) {
      this.scrollBar.unlockScroll()
      this.leaveTopLayer()
    }
  }

  /**
   * Place the element in the top layer via the Popover API so that
   * position: fixed works relative to the viewport regardless of
   * container queries or other containing block ancestors.
   */
  private enterTopLayer() {
    if (typeof this.showPopover !== 'function')
      return

    try {
      this.setAttribute('popover', 'manual')
      this.showPopover()
    }
    catch {
      this.removeAttribute('popover')
    }
  }

  private leaveTopLayer() {
    this.removeAttribute('popover')
  }

  /**
   * Get the position of the element toggling the popout
   * and position the popout underneath it, taking into account the optional placement.
   */
  private updatePosition = async () => {
    if (!this.anchorElement) {
      return
    }

    const computePositionConfig: ComputePositionConfig = {
      strategy: 'fixed',
    }

    if (this.position === 'auto') {
      computePositionConfig.middleware = [
        offset({ mainAxis: this.sideOffset, alignmentAxis: this.alignOffset }),
        autoPlacement({ alignment: this.align, padding: 8 }),
        shift({
          padding: 8,
        }),
        hide(),
      ]
    }
    else {
      computePositionConfig.placement = logicalToPhysical(this.position, this.align, this.direction.dir)
      computePositionConfig.middleware = [
        offset({ mainAxis: this.sideOffset, alignmentAxis: this.alignOffset }),
        flip(),
        shift({
          padding: 8,
        }),
        hide(),
      ]
    }

    const { x, y, placement, middlewareData } = await computePosition(this.anchorElement, this, computePositionConfig)

    this.computedPosition = placement

    // use physical properties here since floating-ui
    // works exclusively in physical dimensions
    // we do all the mapping in logicalToPhysical
    this.style.setProperty('--_n-popout-position-x', `${x}px`)
    this.style.setProperty('--_n-popout-position-y', `${y}px`)

    if (middlewareData.hide?.referenceHidden) {
      this.hide()
    }
  }

  /**
   * Toggle the popout open or closed using state.
   * Updating the position to underneath the target button before the popout is opened.
   */
  private toggleOpen = (e: Event) => {
    e.preventDefault()
    if (this.open) {
      this.hide(false)
    }
    else if (!this.smallViewport || this.alwaysFloating) {
      this.enterTopLayer()
      this.updatePosition().then(() => this.show())
    }
    else {
      this.show()
    }
  }

  private getToggle() {
    const rootNode = this.getRootNode() as Document | ShadowRoot
    const allToggles = rootNode.querySelectorAll<HTMLElement>(`[aria-controls='${this.id}']`)

    let toggle = allToggles[0]

    // When multiple toggles share the same aria-controls (duplicate IDs on page),
    // pair each toggle with its corresponding popout by document order.
    if (allToggles.length > 1) {
      const index = [...rootNode.querySelectorAll<HTMLElement>(`[id='${this.id}']`)].indexOf(this)
      if (index >= 0 && index < allToggles.length) {
        toggle = allToggles[index]
      }
    }

    if (toggle instanceof HTMLSlotElement) {
      return toggle.assignedElements()[0] as HTMLElement
    }

    return toggle
  }

  private getAnchor() {
    if (!this.anchor) {
      return this.targetElement
    }

    const rootNode = this.getRootNode() as Document | ShadowRoot
    const anchor = <HTMLElement>rootNode.querySelector(`#${this.anchor}`)

    if (anchor instanceof HTMLSlotElement) {
      return anchor.assignedElements()[0] as HTMLElement
    }

    return anchor
  }

  /**
   * Update the smallViewport flag to switch between "sheet" and "floating".
   * autoUpdate is needed when a viewport gets larger and the popout is open.
   */
  private handleMediaQueryChange = () => {
    this.smallViewport = mediaQuery.matches

    this.cleanupAutoUpdate?.()

    const sheetMode = this.smallViewport && !this.alwaysFloating

    // Keyboard tracking only applies to the sheet; drop it when switching to the
    // floating design (larger viewport or `always-floating`).
    if (!sheetMode) {
      this.stopKeyboardInsetTracking()
    }

    if (!sheetMode && this.open && this.anchorElement) {
      this.cleanupAutoUpdate = autoUpdate(this.anchorElement, this, this.updatePosition)
      this.scrollBar.unlockScroll()
    }
    else if (this.open) {
      this.scrollBar.lockScroll()

      if (sheetMode) {
        this.trackKeyboardInset()
      }
    }
  }

  /**
   * Track the on-screen keyboard via the VisualViewport API and expose its
   * height as `--_n-popout-keyboard-inset` on the host. In sheet mode the popout
   * is `position: fixed` against the layout viewport, which does not shrink when
   * a mobile keyboard opens — so without this the sheet, and content that
   * filters inside it (e.g. a `nord-dropdown`), ends up behind the keyboard. The
   * token is inherited by both the sheet (`.n-popout`) and any slotted content,
   * letting each offset above the keyboard and cap its height to the visible
   * area. (NDS-102)
   */
  private trackKeyboardInset() {
    const viewport = typeof window === 'undefined' ? undefined : window.visualViewport
    if (!viewport || this.cleanupViewportTracking) {
      return
    }

    const update = () => {
      const inset = computeKeyboardInset(window.innerHeight, viewport)
      this.style.setProperty('--_n-popout-keyboard-inset', `${inset}px`)
    }

    update()
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)

    this.cleanupViewportTracking = () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
      this.style.removeProperty('--_n-popout-keyboard-inset')
      this.cleanupViewportTracking = undefined
    }
  }

  private stopKeyboardInsetTracking() {
    this.cleanupViewportTracking?.()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-popout': Popout
  }
}
